///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";

import AutoComplete from "../form-controls/AutoComplete";
import OutlinedInput from "../form-controls/OutlinedInput";
import {
  bidStrategyTypes,
  bidTypes,
  division,
  modeType,
  shipperTenderStrategyTypes,
  userRole,
  bidScacType
} from "../../helpers/types";
import messages from "../../helpers/messages";

function Strategy({
  type,
  modeDetails,
  setModeDetails,
  validationData,
  removeError,
  bidType,
  userOrBidDivision,
  validateStrategicQuantile,
  bidScac,
  isReview
}) {
  const {
    mode,
    bidStrategy,
    shipperTenderStrategy,
    expectedLanesPerDay,
    goalLanesPerDay,
    acceptanceParameters,
    strategyNotes,
    strategicQuantile
  } = modeDetails;
  const {
    featureFlags: { useStrategicQuantile, crmEnableImdlUi },
    user: {
      user: { role }
    },
    strategicQuantile: { strategicQuantile: strategicQuantileMinMax }
  } = useSelector(state => state);
  const intl = useIntl();

  const coyoteFleetBidType = bidTypes.find(
    x => x.label === messages.coyoteFleet.defaultMessage
  ).value;

  const showStrategicQuantile =
    useStrategicQuantile &&
    type === modeType.tl &&
    (role === userRole.tlPricingAnalyst ||
      role === userRole.management ||
      role === userRole.assetBasedPricingUser) &&
    userOrBidDivision === division.coy;

  useEffect(() => {
    if (bidType?.value === coyoteFleetBidType && type === modeType.tl) {
      setModeDetails(
        mode,
        "bidStrategy",
        bidStrategyTypes.find(
          x => x.label === messages.primaryOnly.defaultMessage
        )
      );
      setModeDetails(
        mode,
        "shipperTenderStrategy",
        shipperTenderStrategyTypes.find(
          x => x.label === messages.primaryBackup.defaultMessage
        )
      );
      setModeDetails(mode, "expectedLanesPerDay", 1);
      setModeDetails(mode, "goalLanesPerDay", 5);
      setModeDetails(mode, "acceptanceParameters", "N/A");
      setModeDetails(mode, "strategyNotes", "N/A");
    }
  }, [bidType, coyoteFleetBidType, mode, setModeDetails, type]);

  const handleChange = (event, mode) => {
    if (
      !event.target.validity.patternMismatch &&
      parseInt(event.target.value, 10) !== parseInt(event.target.min, 10)
    ) {
      setModeDetails(
        mode,
        event.target.name,
        event.target.value
          ? parseInt(event.target.value, 10)
          : event.target.value
      );
    }
  };

  const handleStrategicQuantileChange = (event, mode) => {
    if (!event.target.validity.patternMismatch) {
      setModeDetails(
        mode,
        event.target.name,
        event.target.value
          ? parseInt(event.target.value, 10)
          : event.target.value
      );
    }
  };

  const validationProps = field => ({
    onFocus: () => !validationData[field].isValid && removeError(type, field),
    error: !validationData[field].isValid,
    validationMessage: validationData[field].validationMessage
  });

  const handleTrimText = (mode, field, value) => {
    if (value) {
      setModeDetails(mode, field, value.trim());
    } else {
      setModeDetails(mode, field, "");
    }
  };

  const showOriginalNames =
    (bidScac?.value === bidScacType[0].value)
    && !(type === modeType.imdl && bidScac?.value === bidScacType[1].value && crmEnableImdlUi);
  const hideForCrmImdl = crmEnableImdlUi && type === modeType.imdl;

  return (
    <div data-testid={type} className="grid section-grid">
      {!hideForCrmImdl && showOriginalNames ? (        
        <div data-testid={`${type}-bid-strategy`} className="grid__cell one-half">
          <AutoComplete
            label={messages.bidStrategy.defaultMessage}
            handleChange={bidStrategy =>
              setModeDetails(mode, "bidStrategy", bidStrategy)
            }
            value={bidStrategy}
            staticOptions={bidStrategyTypes}
            isDisabled={bidType?.value === coyoteFleetBidType}
            {...validationProps("bidStrategy")}
          />
        </div>
      ) : null}
      {!hideForCrmImdl && showOriginalNames ? (    
        <div
          data-testid={`${type}-shipper-tender-strategy`}
          className="grid__cell one-half"
        >
          <AutoComplete
            label={messages.shipperTenderStrategy.defaultMessage}
            handleChange={shipperTenderStrategy =>
              setModeDetails(mode, "shipperTenderStrategy", shipperTenderStrategy)
            }
            value={shipperTenderStrategy}
            staticOptions={shipperTenderStrategyTypes}
            isDisabled={bidType?.value === coyoteFleetBidType}
            {...validationProps("shipperTenderStrategy")}
          />
        </div>
      ) : null}
      {!hideForCrmImdl && showOriginalNames ? (    
        <div className="grid__cell one-half">
          <OutlinedInput
            data-testid={`${type}-expected-lanes-per-day`}
            name="expectedLanesPerDay"
            value={expectedLanesPerDay}
            label={messages.expectedLPD.defaultMessage}
            pattern="[0-9]*"
            min="0"
            maxLength="4"
            onChange={event => handleChange(event, mode)}
            disabled={bidType?.value === coyoteFleetBidType}
            {...validationProps("expectedLanesPerDay")}
          />
        </div>
      ) : null}
      {!hideForCrmImdl && showOriginalNames ? (  
        <div className="grid__cell one-half">
          <OutlinedInput
            data-testid={`${type}-goal-lanes-per-day`}
            name="goalLanesPerDay"
            value={goalLanesPerDay}
            label={messages.LPDGoal.defaultMessage}
            pattern="[0-9]*"
            min="0"
            maxLength="4"
            onChange={event => handleChange(event, mode)}
            disabled={bidType?.value === coyoteFleetBidType}
            {...validationProps("goalLanesPerDay")}
          />
        </div>
      ) : null}
      {showOriginalNames ? (  
        <div className="grid__cell one-half">
          <OutlinedInput
            data-testid={`${type}-acceptanceParameters`}
            name="acceptanceParameters"
            label={messages.awardAcceptanceParameters.defaultMessage}
            maxLength="3000"
            onChange={event =>
              setModeDetails(mode, "acceptanceParameters", event.target.value)
            }
            isMulti
            value={acceptanceParameters}
            onBlur={event =>
              handleTrimText(mode, "acceptanceParameters", event.target.value)
            }
            {...validationProps("acceptanceParameters")}
            disabled={bidType?.value === coyoteFleetBidType}
            dynamicHeight
          />
        </div>
      ) : (
        <div className="grid__cell one-half">
          <OutlinedInput
            data-testid={`${type}-acceptanceParameters`}
            name="acceptanceParameters"
            label={messages.tenderAcceptanceParameters.defaultMessage}
            maxLength="3000"
            onChange={event =>
              setModeDetails(mode, "acceptanceParameters", event.target.value)
            }
            isMulti
            value={acceptanceParameters}
            onBlur={event =>
              handleTrimText(mode, "acceptanceParameters", event.target.value)
            }
            {...validationProps("acceptanceParameters")}
            disabled={isReview === true}
            dynamicHeight
          />
        </div>
      )}
      {showOriginalNames ? (  
        <div
          className={`grid__cell one-half ${
            showStrategicQuantile ? "mb-16" : "mb-24"
          }`}
        >
          <OutlinedInput
            data-testid={`${type}-strategyNotes`}
            name="strategyNotes"
            label={messages.strategyNotes.defaultMessage}
            maxLength="3000"
            onChange={event =>
              setModeDetails(mode, "strategyNotes", event.target.value)
            }
            isMulti
            value={strategyNotes}
            onBlur={event =>
              handleTrimText(mode, "strategyNotes", event.target.value)
            }
            {...validationProps("strategyNotes")}
            disabled={bidType?.value === coyoteFleetBidType}
            dynamicHeight
          />
        </div>
      ) : (          
        <div
          className={`grid__cell one-half ${
            showStrategicQuantile ? "mb-16" : "mb-24"
          }`}
        >
          <OutlinedInput
            data-testid={`${type}-strategyNotes`}
            name="strategyNotes"
            label={messages.strategyNotesOptional.defaultMessage}
            maxLength="3000"
            onChange={event =>
              setModeDetails(mode, "strategyNotes", event.target.value)
            }
            isMulti
            value={strategyNotes}
            onBlur={event =>
              handleTrimText(mode, "strategyNotes", event.target.value)
            }
            {...validationProps("strategyNotes")}
            disabled={bidType?.value === coyoteFleetBidType}
            dynamicHeight
          />
        </div>
      )}
      {showStrategicQuantile ? (
        <div className="grid__cell one-half mb-24">
          <OutlinedInput
            data-testid={`${type}-strategic-quantile`}
            name="strategicQuantile"
            value={strategicQuantile}
            label={messages.strategicQuantileOptional.defaultMessage}
            pattern="^([0-9]|[1-9][0-9]|100)$"
            unit="%"
            onChange={event => handleStrategicQuantileChange(event, mode)}
            onBlur={() =>
              validateStrategicQuantile(
                strategicQuantileMinMax?.minimumValue,
                strategicQuantileMinMax?.maximumValue
              )
            }
            helpertext={
              strategicQuantileMinMax
                ? intl.formatMessage(
                    messages.strategicQuantileValidationMessage,
                    {
                      minValue: strategicQuantileMinMax?.minimumValue,
                      maxValue: strategicQuantileMinMax?.maximumValue
                    }
                  )
                : ""
            }
            {...validationProps("strategicQuantile")}
          />
        </div>
      ) : null}
    </div>
  );
}

export default Strategy;
